import { ReactElement, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	EuiFieldText,
	EuiFormRow,
	EuiPage,
	EuiPageBody,
	EuiPageHeader,
	EuiSpacer,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import { heapEvent } from '@equipmentshare/heaper'
import {
	DimensionUom,
	ItemMasterVariantUom,
	WeightUnit,
	useAddItemMasterVariantMutation,
} from '../../../../graphql'
import { Part, Provider } from 'graphql'
import ProviderPicker from 'components/ProviderPicker'
import PartPicker from 'components/PartPicker'
import FormFooter, { FORM_FOOTER_HEIGHT } from '../components/FormFooter'
import { isValidSiocQuantity, siocQuantityForPayload, siocToggleChanges, SIOC_QUANTITY_ERROR } from '../utils'

type VariantFields = {
	conversionRate: string,
	dimensionLength: string,
	dimensionWidth: string,
	dimensionHeight: string,
	dimensionUnit: DimensionUom,
	weightAmount: string,
	weightUnit: WeightUnit,
	freightClass: string,
	nmfc: string,
	shippedInOriginalContainer: boolean,
	siocQuantity: string,
	isPolybagEligible: boolean,
	uom: ItemMasterVariantUom,
}

const defaultVariantFields: VariantFields = {
	conversionRate: '',
	dimensionLength: '',
	dimensionWidth: '',
	dimensionHeight: '',
	dimensionUnit: DimensionUom.In,
	weightAmount: '',
	weightUnit: WeightUnit.Lb,
	freightClass: '',
	nmfc: '',
	shippedInOriginalContainer: false,
	siocQuantity: '',
	isPolybagEligible: false,
	uom: ItemMasterVariantUom.Each,
}

function VariantFieldsForm( {
	fields,
	onChange,
}: {
	fields: VariantFields,
	onChange: ( updated: Partial<VariantFields> ) => void,
} ): ReactElement {
	return (
		<Box>
			<Typography variant="subtitle2" mb={2}>Variant Details</Typography>
			<Stack spacing={2}>
				<TextField
					label="Conversion Rate"
					value={fields.conversionRate}
					onChange={( e ) => onChange( { conversionRate: e.target.value } )}
					size="small"
					type="number"
					helperText="Must be 2 or greater (number of base units per this variant)"
					inputProps={{ min: 2 }}
				/>
				<Box>
					<Typography variant="caption" color="text.secondary">Unit of Measure</Typography>
					<Select
						value={fields.uom}
						onChange={( e ) => onChange( { uom: e.target.value as ItemMasterVariantUom } )}
						size="small"
						fullWidth
					>
						{Object.values( ItemMasterVariantUom ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Box>
				<Stack direction="row" spacing={2}>
					<TextField
						label="Length"
						value={fields.dimensionLength}
						onChange={( e ) => onChange( { dimensionLength: e.target.value } )}
						size="small"
						type="number"
					/>
					<TextField
						label="Width"
						value={fields.dimensionWidth}
						onChange={( e ) => onChange( { dimensionWidth: e.target.value } )}
						size="small"
						type="number"
					/>
					<TextField
						label="Height"
						value={fields.dimensionHeight}
						onChange={( e ) => onChange( { dimensionHeight: e.target.value } )}
						size="small"
						type="number"
					/>
					<Select
						value={fields.dimensionUnit}
						onChange={( e ) => onChange( { dimensionUnit: e.target.value as DimensionUom } )}
						size="small"
					>
						{Object.values( DimensionUom ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Stack>
				<Stack direction="row" spacing={2} alignItems="center">
					<TextField
						label="Weight"
						value={fields.weightAmount}
						onChange={( e ) => onChange( { weightAmount: e.target.value } )}
						size="small"
						type="number"
					/>
					<Select
						value={fields.weightUnit}
						onChange={( e ) => onChange( { weightUnit: e.target.value as WeightUnit } )}
						size="small"
					>
						{Object.values( WeightUnit ).map( ( u ) => (
							<MenuItem key={u} value={u}>{u}</MenuItem>
						) )}
					</Select>
				</Stack>
				<TextField
					label="Freight Class"
					value={fields.freightClass}
					onChange={( e ) => onChange( { freightClass: e.target.value } )}
					size="small"
				/>
				<TextField
					label="NMFC"
					value={fields.nmfc}
					onChange={( e ) => onChange( { nmfc: e.target.value } )}
					size="small"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.shippedInOriginalContainer}
							onChange={( e ) => onChange( siocToggleChanges( e.target.checked ) )}
						/>
					}
					label="Shipped in Original Container"
				/>
				<TextField
					label="SIOC Quantity"
					value={fields.shippedInOriginalContainer ? fields.siocQuantity : ''}
					onChange={( e ) => onChange( { siocQuantity: e.target.value } )}
					size="small"
					type="number"
					disabled={!fields.shippedInOriginalContainer}
					required={fields.shippedInOriginalContainer}
					error={!isValidSiocQuantity( fields )}
					helperText={ !isValidSiocQuantity( fields ) ? SIOC_QUANTITY_ERROR : undefined }
					inputProps={{ min: 1 }}
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={fields.isPolybagEligible}
							onChange={( e ) => onChange( { isPolybagEligible: e.target.checked } )}
							disabled={fields.shippedInOriginalContainer}
						/>
					}
					label="Polybag Eligible"
				/>
			</Stack>
		</Box>
	)
}

function ExistingProductMode( { itemMasterId }: { itemMasterId: string } ): ReactElement {
	const navigate = useNavigate()
	const [ selectedProduct, setSelectedProduct ] = useState<Part | undefined>( undefined )
	const [ fields, setFields ] = useState<VariantFields>( defaultVariantFields )

	const [ addVariant, { loading } ] = useAddItemMasterVariantMutation()

	const handleFieldChange = ( updated: Partial<VariantFields> ) => {
		setFields( ( prev ) => ( { ...prev, ...updated } ) )
	}

	const handleSubmit = async() => {
		if ( !selectedProduct || !fields.conversionRate || !isValidSiocQuantity( fields ) ) {
			return
		}

		try {
			const result = await addVariant( {
				variables: {
					input: {
						itemMasterId,
						variant: {
							productId: selectedProduct.id.toString(),
							conversionRate: Number( fields.conversionRate ),
							uom: fields.uom,
							dimensions: {
								length: Number( fields.dimensionLength ),
								width: Number( fields.dimensionWidth ),
								height: Number( fields.dimensionHeight ),
								unit: fields.dimensionUnit,
							},
							weight: {
								amount: Number( fields.weightAmount ),
								unit: fields.weightUnit,
							},
							freightClass: fields.freightClass || undefined,
							nmfc: fields.nmfc || undefined,
							shippedInOriginalContainer: fields.shippedInOriginalContainer,
							siocQuantity: siocQuantityForPayload( fields ),
							isPolybagEligible: fields.isPolybagEligible,
						},
					},
				},
			} )

			if ( result.data?.addItemMasterVariant?.success ) {
				heapEvent( 'Inventory - Item Master - Add Variant - Submit - Success', { item_master_id: itemMasterId } )
				navigate( `/manage/item-masters/${itemMasterId}` )
			} else {
				heapEvent( 'Inventory - Item Master - Add Variant - Submit - Error', {} )
			}
		} catch {
			heapEvent( 'Inventory - Item Master - Add Variant - Submit - Error', {} )
		}
	}

	const isDisabled = loading || !selectedProduct || !fields.conversionRate ||
		!fields.dimensionLength || !fields.dimensionWidth || !fields.dimensionHeight || !fields.weightAmount ||
		!isValidSiocQuantity( fields )

	return (
		<Stack spacing={3} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
			<Box>
				<Typography variant="subtitle2" mb={2}>Select Product</Typography>
				<PartPicker
					label="Product"
					selectedPart={selectedProduct}
					onChange={( _id, part ) => setSelectedProduct( part )}
					required
				/>
			</Box>

			<VariantFieldsForm fields={fields} onChange={handleFieldChange} />

			<FormFooter>
				<Button variant="outlined" onClick={() => navigate( `/manage/item-masters/${itemMasterId}` )} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={isDisabled}
				>
					{loading ? 'Adding...' : 'Add Variant'}
				</Button>
			</FormFooter>
		</Stack>
	)
}

function NewProductMode( { itemMasterId }: { itemMasterId: string } ): ReactElement {
	const navigate = useNavigate()
	const [ manufacturer, setManufacturer ] = useState<Provider | undefined>( undefined )
	const [ manufacturerNumber, setManufacturerNumber ] = useState( '' )
	const [ productName, setProductName ] = useState( '' )
	const [ fields, setFields ] = useState<VariantFields>( defaultVariantFields )

	const [ addVariant, { loading } ] = useAddItemMasterVariantMutation()

	const handleFieldChange = ( updated: Partial<VariantFields> ) => {
		setFields( ( prev ) => ( { ...prev, ...updated } ) )
	}

	const handleSubmit = async() => {
		if ( !manufacturer || !manufacturerNumber || !productName || !fields.conversionRate || !isValidSiocQuantity( fields ) ) {
			return
		}

		try {
			const result = await addVariant( {
				variables: {
					input: {
						itemMasterId,
						variant: {
							newProduct: {
								manufacturerId: manufacturer.id.toString(),
								manufacturerNumber,
								name: productName,
							},
							conversionRate: Number( fields.conversionRate ),
							uom: fields.uom,
							dimensions: {
								length: Number( fields.dimensionLength ),
								width: Number( fields.dimensionWidth ),
								height: Number( fields.dimensionHeight ),
								unit: fields.dimensionUnit,
							},
							weight: {
								amount: Number( fields.weightAmount ),
								unit: fields.weightUnit,
							},
							freightClass: fields.freightClass || undefined,
							nmfc: fields.nmfc || undefined,
							shippedInOriginalContainer: fields.shippedInOriginalContainer,
							siocQuantity: siocQuantityForPayload( fields ),
							isPolybagEligible: fields.isPolybagEligible,
						},
					},
				},
			} )

			if ( result.data?.addItemMasterVariant?.success ) {
				heapEvent( 'Inventory - Item Master - Add Variant - Submit - Success', { item_master_id: itemMasterId } )
				navigate( `/manage/item-masters/${itemMasterId}` )
			} else {
				heapEvent( 'Inventory - Item Master - Add Variant - Submit - Error', {} )
			}
		} catch {
			heapEvent( 'Inventory - Item Master - Add Variant - Submit - Error', {} )
		}
	}

	const isDisabled = loading || !manufacturer || !manufacturerNumber || !productName || !fields.conversionRate ||
		!fields.dimensionLength || !fields.dimensionWidth || !fields.dimensionHeight || !fields.weightAmount ||
		!isValidSiocQuantity( fields )

	return (
		<Stack spacing={3} sx={{ pb: `${FORM_FOOTER_HEIGHT}px` }}>
			<Box>
				<Typography variant="subtitle2" mb={2}>New Product Details</Typography>
				<Stack spacing={2}>
					<ProviderPicker
						label="Manufacturer"
						onChange={( _id, provider ) => setManufacturer( provider )}
						selectedProvider={manufacturer}
						required
					/>
					<EuiFormRow label="Manufacturer Number">
						<EuiFieldText
							value={manufacturerNumber}
							onChange={( e ) => setManufacturerNumber( e.target.value )}
						/>
					</EuiFormRow>
					<EuiFormRow label="Product Name">
						<EuiFieldText
							value={productName}
							onChange={( e ) => setProductName( e.target.value )}
						/>
					</EuiFormRow>
				</Stack>
			</Box>

			<VariantFieldsForm fields={fields} onChange={handleFieldChange} />

			<FormFooter>
				<Button variant="outlined" onClick={() => navigate( `/manage/item-masters/${itemMasterId}` )} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={isDisabled}
				>
					{loading ? 'Adding...' : 'Add Variant'}
				</Button>
			</FormFooter>
		</Stack>
	)
}

export default function ItemMasterAddVariant(): ReactElement {
	const { id = '' } = useParams<{ id: string }>()
	const [ mode, setMode ] = useState( 0 )

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody>
				<EuiPageHeader pageTitle="Add Variant" paddingSize="none" />
				<EuiSpacer />

				<Tabs value={mode} onChange={( _, v ) => setMode( v )}>
					<Tab label="Existing Product" />
					<Tab label="New Product" />
				</Tabs>

				<Box mt={3}>
					{mode === 0
						? <ExistingProductMode itemMasterId={id} />
						: <NewProductMode itemMasterId={id} />}
				</Box>
			</EuiPageBody>
		</EuiPage>
	)
}
